<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

    <!-- Loading skeleton — mirrors the loaded layout -->
    <div v-if="state.loading && !state.fetchedAt" class="animate-pulse divide-y divide-gray-100 dark:divide-gray-700">
      <div v-for="n in 4" :key="n" class="flex items-center gap-3 px-4 sm:px-6 py-4">
        <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
        <div class="space-y-2">
          <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-2.5 w-14 bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
        <div class="flex-1 space-y-2">
          <div class="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-2.5 w-1/4 bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
        <div class="h-5 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="state.error" class="flex flex-col items-center justify-center py-24 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ state.error }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
        Try again
      </button>
    </div>

    <!-- Empty -->
    <div v-else-if="!state.items.length" class="flex flex-col items-center justify-center py-24 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ emptyMessage }}</h3>
    </div>

    <!-- Loaded -->
    <template v-else>
      <div v-for="(items, date) in groupedItems" :key="date">
        <div class="px-4 sm:px-6 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-700">
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">{{ date }}</p>
        </div>
        <BookingRow v-for="item in items" :key="item.id" :booking="item" :category="category" :event-id="eventId" :flash="flash" />
      </div>

      <div v-if="state.page.total_pages > 1" class="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700">
        <span class="text-xs text-gray-400 dark:text-gray-500">
          Page {{ state.page.current_page }} of {{ state.page.total_pages }}
        </span>
        <div class="flex gap-1.5">
          <button @click="goToPage(state.page.current_page - 1)" :disabled="state.page.current_page <= 1"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-30 disabled:cursor-not-allowed border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
            </svg>
            Prev
          </button>
          <button @click="goToPage(state.page.current_page + 1)" :disabled="state.page.current_page >= state.page.total_pages"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-30 disabled:cursor-not-allowed border-blue-600 bg-blue-600 text-white hover:bg-blue-700">
            Next
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, watch, onMounted } from 'vue'
import { useConstants } from 'composables/useConstants.js'
import { useEventBookingsStore } from 'stores/eventBookings.js'
import BookingRow from 'controllers/vue_components/events/bookings/BookingRow'

const props = defineProps({
  eventId:  { type: [String, Number], required: true },
  category: { type: String, required: true },
  flash:    { type: Object, default: null },
  dateFrom: { type: String, default: null },
  dateTo:   { type: String, default: null },
})

const { I18N } = useConstants()
const store = useEventBookingsStore()

const state = computed(() => store.categoryState(props.eventId, props.category))

const EMPTY_MESSAGES = {
  recent:   'No recent bookings yet',
  upcoming: I18N.application.bookings.upcoming.no_upcoming,
  past:     I18N.application.bookings.past.no_past,
  canceled: I18N.application.bookings.canceled.no_canceled,
  archived: I18N.application.bookings.archived.no_archived,
}
const emptyMessage = computed(() => EMPTY_MESSAGES[props.category] || EMPTY_MESSAGES.upcoming)

const groupedItems = computed(() =>
  state.value.items.reduce((groups, item) => {
    const date = item.host_formatted_target_date
    if (!groups[date]) groups[date] = []
    groups[date].push(item)
    return groups
  }, {})
)

function load() {
  store.fetchIfStale(props.eventId, props.category, 1, props.dateFrom, props.dateTo)
}

function retry() {
  store.invalidateCategory(props.eventId, props.category)
  store.fetchCategory(props.eventId, props.category, 1, props.dateFrom, props.dateTo)
}

function goToPage(page) {
  if (page < 1 || page > state.value.page.total_pages) return
  store.fetchCategory(props.eventId, props.category, page, props.dateFrom, props.dateTo)
}

onMounted(load)
watch(() => props.category, load)
watch(() => [props.dateFrom, props.dateTo], load)
</script>
